import { z } from 'zod'

const Revision = z.string().regex(/^[a-f0-9]{64}$/)
const PageSize = z.coerce.number().int().min(1).max(100).default(50)
export const SeriesModelLink = z.object({
  catalogClassId: z.string().nullable(),
  catalogSubtypeId: z.string().nullable(),
  divisionCode: z.string().nullable(),
  catalogClass: z.object({ name: z.string() }).nullable().optional(),
  catalogSubtype: z.object({ name: z.string() }).nullable().optional(),
})
export const SeriesReviewReason = z.enum([
  'model_required',
  'legacy_identity',
  'first_identity',
  'division_changed',
  'multiple_candidates',
  'edition_collision',
  'retired',
  'explicit_unmatched',
])
export const SeriesIdentity = z.object({
  id: z.guid(),
  seriesId: z.guid(),
  trendKey: z.string(),
  label: z.string(),
  retired: z.boolean(),
  revision: z.number().int().nonnegative(),
  ...SeriesModelLink.shape,
})
export const CreateSeriesIdentityRequest = z
  .object({
    label: z.string().trim().min(1).max(200),
    catalogClassId: z.string().min(1),
    catalogSubtypeId: z.string().min(1).nullable().default(null),
    divisionCode: z.string().trim().min(1).max(200),
  })
  .strict()
export const UpdateSeriesIdentityRequest = z
  .object({
    label: z.string().trim().min(1).max(200).optional(),
    retired: z.boolean().optional(),
    expectedRevision: z.number().int().nonnegative(),
  })
  .strict()
export const DeleteSeriesIdentityRequest = z
  .object({ expectedRevision: z.number().int().nonnegative() })
  .strict()
export const SeriesIdentityResponse = z.object({ identity: SeriesIdentity })
export const SeriesAlignmentQuery = z
  .object({
    years: z
      .string()
      .regex(/^\d{4}(,\d{4}){0,9}$/)
      .optional(),
    fromYear: z.coerce.number().int().min(1000).max(9999).optional(),
    toYear: z.coerce.number().int().min(1000).max(9999).optional(),
    divisionCode: z.string().min(1).max(200).optional(),
    state: z.enum(['all', 'unresolved', 'unmatched']).default('all'),
    limit: PageSize,
    cursor: z.string().max(1000).optional(),
    trayLimit: PageSize,
    trayCursor: z.string().max(1000).optional(),
  })
  .strict()
export const SeriesSuggestionsQuery = SeriesAlignmentQuery.omit({
  cursor: true,
  trayCursor: true,
  trayLimit: true,
  state: true,
}).extend({
  targetShowId: z.guid(),
  cursor: z.string().max(1000).optional(),
})
export const SeriesEvidenceEdition = z.object({
  showId: z.guid(),
  year: z.number().int(),
  name: z.string(),
})
export const SeriesIdentityAssignmentsQuery = z
  .object({ identityId: z.guid(), cursor: z.string().max(1000).optional(), limit: PageSize })
  .strict()
export const SeriesIdentityAssignmentsResponse = z.object({
  accessRevision: Revision,
  assignments: z.array(z.object({ id: z.guid(), showId: z.guid(), identityId: z.guid() })).max(100),
  editions: z.array(SeriesEvidenceEdition).max(100),
  nextCursor: z.string().nullable(),
})
export type SeriesIdentityAssignmentsOutput = z.infer<typeof SeriesIdentityAssignmentsResponse>

export const SeriesDecisionState = z.enum(['assigned', 'unmatched', 'unresolved'])
export const SeriesAlignmentClass = z.object({
  id: z.guid(),
  showId: z.guid(),
  code: z.string(),
  originalName: z.string(),
  effectiveName: z.string(),
  catalogClassId: z.string().nullable(),
  catalogSubtypeId: z.string().nullable().optional(),
  order: z.number().int(),
  division: z.object({ id: z.guid(), code: z.string(), name: z.string(), order: z.number().int() }),
  count: z.number().int().nonnegative(),
  state: SeriesDecisionState,
  identityId: z.guid().nullable(),
  decisionRevision: z.number().int().nonnegative().nullable(),
  reviewedDivisionCode: z.string().nullable().optional(),
  reviewReasons: z.array(SeriesReviewReason).optional(),
})
export const SeriesAlignmentEdition = z.object({
  showId: z.guid(),
  year: z.number().int(),
  name: z.string(),
  slug: z.string(),
  status: z.enum(['draft', 'accepting_entries', 'judging', 'finalized']),
  canEdit: z.boolean(),
  revision: Revision,
  membershipRevision: z.number().int(),
  unresolvedCount: z.number().int().nonnegative(),
  unmatchedCount: z.number().int().nonnegative(),
})
export const SeriesAlignmentResponse = z.object({
  reconciliation: z.object({ showIds: z.array(z.guid()).max(10), revision: Revision }).optional(),
  editions: z.array(SeriesAlignmentEdition),
  rows: z.array(
    z.object({
      identity: SeriesIdentity,
      cells: z.array(z.object({ showId: z.guid(), class: SeriesAlignmentClass.nullable() })),
    })
  ),
  nextCursor: z.string().nullable(),
  tray: z.object({ classes: z.array(SeriesAlignmentClass), nextCursor: z.string().nullable() }),
})
export const SeriesMatchMethod = z.enum([
  'catalog',
  'catalog_in_division',
  'custom_key',
  'name_in_division',
])
export const SeriesSuggestionEvidence = z.object({
  from: SeriesAlignmentClass,
  to: SeriesAlignmentClass,
  method: SeriesMatchMethod,
})
export const SeriesSuggestionsResponse = z.object({
  editions: z.array(SeriesAlignmentEdition),
  suggestions: z.array(
    z.object({
      class: SeriesAlignmentClass,
      state: z.enum(['none', 'suggested', 'ambiguous', 'unmatched']),
      candidates: z.array(
        z.object({
          identityId: z.guid(),
          identityRevision: z.number().int().nonnegative(),
          label: z.string(),
          evidence: z.array(SeriesSuggestionEvidence),
          warnings: z.array(
            z.enum([
              'chain',
              'multiple_candidates',
              'edition_collision',
              'retired',
              'model_mismatch',
              'legacy_identity',
              'division_changed',
            ])
          ),
        })
      ),
    })
  ),
  nextCursor: z.string().nullable(),
})
export const UpdateSeriesAlignmentRequest = z
  .object({
    operations: z
      .array(
        z
          .object({
            showId: z.guid(),
            classId: z.guid(),
            state: SeriesDecisionState,
            identityId: z.guid().optional(),
            acknowledgeDivisionChange: z.boolean().default(false),
          })
          .strict()
      )
      .min(1)
      .max(1000),
    editionRevisions: z.record(z.guid(), Revision),
    identityRevisions: z.record(z.guid(), z.number().int().nonnegative()),
  })
  .strict()
export const UpdateSeriesAlignmentResponse = z.object({ editions: z.array(SeriesAlignmentEdition) })
export const ReconcileSeriesAlignmentRequest = z
  .object({
    showIds: z
      .array(z.guid())
      .min(1)
      .max(10)
      .refine(ids => new Set(ids).size === ids.length),
    expectedRevision: Revision,
  })
  .strict()
export const ReconcileSeriesAlignmentResponse = z.object({
  assignedCount: z.number().int().nonnegative(),
})
export type SeriesAlignmentQueryInput = z.infer<typeof SeriesAlignmentQuery>
export type SeriesAlignmentOutput = z.infer<typeof SeriesAlignmentResponse>
export type CreateSeriesIdentityInput = z.infer<typeof CreateSeriesIdentityRequest>
export type UpdateSeriesIdentityInput = z.infer<typeof UpdateSeriesIdentityRequest>
export type SeriesSuggestionsQueryInput = z.infer<typeof SeriesSuggestionsQuery>
export type SeriesAlignmentClassOutput = z.infer<typeof SeriesAlignmentClass>
export type SeriesAlignmentEditionOutput = z.infer<typeof SeriesAlignmentEdition>
export type SeriesIdentityOutput = z.infer<typeof SeriesIdentity>
export type SeriesSuggestionsOutput = z.infer<typeof SeriesSuggestionsResponse>
export type UpdateSeriesAlignmentInput = z.infer<typeof UpdateSeriesAlignmentRequest>
